/**
 * The ONE owner of the back stack (README §11.12, 2026-10-15).
 *
 * Like a native app, every layer that opens on top of the page gets its own
 * step in the browser history, so the phone's Back button (and swipe-back)
 * closes the top layer instead of leaving the page:
 *   - every modal <dialog> of the theme, found automatically: cart drawer,
 *     search, quick add, filters, photo zoom, pop-ups, size charts…;
 *   - the ☰ menu (header-drawer);
 *   - the bottom bar's Categories / Account screens (assets/custom-tab-bar.js,
 *     through `backStack`).
 * Closing a layer any other way (✕, backdrop, Esc, a tab) takes its step back
 * out of the history, so Back never has to be pressed twice. Steps are only
 * removed when they are the current history entry: an entry Horizon pushed on
 * top (filters write ?filter… into the address) is never popped by mistake.
 *
 * History states carry `qBack` = how many layers were open at that entry; on
 * Back, every layer above the new depth closes, top first. Layers may store
 * what they are in the state (`extra`), so a page that is reloaded on an
 * entry (no back-forward cache — e.g. coming back from Shopify's account
 * pages) can reopen them and `adopt` their steps: history and screen agree.
 */

/** @type {{ key: unknown, close: () => void }[]} open layers, bottom → top */
const entries = [];
/** history.back() calls we made ourselves; their popstate is not a Back press */
let ownBacks = 0;
/** @type {(() => void)[]} */
let waiters = [];

const currentDepth = () => {
  const state = history.state;
  return state && typeof state === 'object' && typeof state.qBack === 'number' ? state.qBack : 0;
};

export const backStack = {
  /**
   * A layer opened: give it a Back step (once).
   * @param {unknown} key
   * @param {() => void} close - closes the layer when Back is pressed
   * @param {Record<string, unknown>} [extra] - kept in the history state (to reopen it after a reload)
   */
  open(key, close, extra = {}) {
    if (entries.some((entry) => entry.key === key)) return;
    entries.push({ key, close });
    const state = history.state && typeof history.state === 'object' ? history.state : {};
    history.pushState({ ...state, ...extra, qBack: entries.length }, '');
  },

  /**
   * A layer reopened on page load for a step that is already in the history
   * (the page was reloaded on it): register it without a new step.
   * @param {unknown} key
   * @param {() => void} close
   */
  adopt(key, close) {
    if (entries.some((entry) => entry.key === key)) return;
    if (entries.length >= currentDepth()) return; // no step left to adopt
    entries.push({ key, close });
  },

  /** The current history state (what the layers stored) */
  get state() {
    return history.state && typeof history.state === 'object' ? history.state : {};
  },

  /**
   * A layer was closed by the page itself (✕, backdrop, Esc, another tab):
   * remove its Back step when it is the current history entry.
   * @param {unknown} key
   */
  closed(...keys) {
    // Several layers at once (a screen and its sub-screen): one history.go(-n)
    const before = entries.length;
    let fromTop = 0;
    for (const key of [...keys].reverse()) {
      const index = entries.findIndex((entry) => entry.key === key);
      if (index === -1) continue;
      if (index === entries.length - 1) fromTop += 1;
      entries.splice(index, 1);
    }
    if (fromTop > 0 && currentDepth() === before) {
      ownBacks += 1;
      history.go(-fromTop);
    }
  },

  /** @param {unknown} key */
  has(key) {
    return entries.some((entry) => entry.key === key);
  },

  /** Resolves once our own history.back() calls have landed (before pushing a new step) */
  settled() {
    return ownBacks === 0 ? Promise.resolve() : new Promise((resolve) => waiters.push(resolve));
  },
};

window.addEventListener('popstate', () => {
  if (ownBacks > 0) {
    ownBacks -= 1;
    if (ownBacks === 0) waiters.splice(0).forEach((resolve) => resolve());
    return;
  }
  // Back (or swipe-back): close every layer above the entry we landed on, top first
  const depth = currentDepth();
  while (entries.length > depth) {
    const entry = entries.pop();
    try {
      entry?.close();
    } catch (error) {
      console.error(error);
    }
  }
});

/* ---- Theme overlays, found automatically --------------------------------- */

/** @param {Element} element */
const isMenuDrawer = (element) => element.matches('header-drawer > details, header-drawer details.menu-drawer-container');

/** Closes an overlay the way its own component does (animations, focus, scroll lock) */
function closeOverlay(/** @type {Element} */ element) {
  if (element instanceof HTMLDetailsElement) {
    const drawer = /** @type {any} */ (element.closest('header-drawer'));
    if (typeof drawer?.close === 'function') drawer.close();
    else element.removeAttribute('open');
    return;
  }
  const themeDrawer = /** @type {any} */ (element.closest('theme-drawer'));
  if (typeof themeDrawer?.close === 'function') {
    themeDrawer.close();
    return;
  }
  /** @type {any} */
  let host = element.parentElement;
  while (host && typeof host.closeDialog !== 'function') host = host.parentElement;
  if (host) host.closeDialog();
  else /** @type {HTMLDialogElement} */ (element).close();
}

new MutationObserver((records) => {
  for (const record of records) {
    const element = record.target;
    if (!(element instanceof Element)) continue;

    if (element instanceof HTMLDialogElement) {
      if (element.open && element.matches(':modal')) backStack.open(element, () => closeOverlay(element));
      else if (!element.open) backStack.closed(element);
    } else if (element instanceof HTMLDetailsElement && isMenuDrawer(element)) {
      if (element.open) backStack.open(element, () => closeOverlay(element));
      else backStack.closed(element);
    }
  }
}).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
